import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { fetchJson } from '~/utils/fetch-json-v2';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '~/utils/local-storage';

import type { User } from './types';

export type AuthContextProps = {
  loading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  user: User | null;
  verifySession: () => Promise<User | null>;
  setAccessToken: (token: string, redirect?: boolean) => Promise<void>;
  updateMetadata: (metadata: Record<string, unknown>) => Promise<void>;
};

export const AuthContext = createContext<AuthContextProps | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const isAuthenticated = currentUser !== null;

  const getCurrentUser = useCallback(async (token: string) => {
    const { data: res } = await fetchJson<User>('/identities/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res) return null;

    const user: User = {
      ...res,
      profile: {
        ...res.profile,
        full_name: `${res.profile.first_name} ${res.profile.last_name}`,
      },
    };

    setCurrentUser(user);

    return user;
  }, []);

  const updateMetadata = useCallback(
    async (metadata: Record<string, unknown>) => {
      const { data: res } = await fetchJson<{ result: { identity: User } }>(
        '/identities/me',
        {
          method: 'PATCH',
          body: {
            metadata,
          },
        },
      );
      if (!res) return;

      const identity = res.result.identity;

      const user = {
        ...identity,
        profile: {
          ...identity.profile,
          full_name: `${identity.profile.first_name} ${identity.profile.last_name}`,
        },
      };
      setCurrentUser(user);
    },
    [],
  );

  const setAccessToken = useCallback(
    async (token: string, redirect = true) => {
      const res = await getCurrentUser(token);
      if (!res) {
        throw new Error('Opps, algo salió mal, vuelve a intentarlo');
      }
      setLocalStorageItem('access_token', token);
      if (redirect) {
        window.location.href = '/';
      }
    },
    [getCurrentUser],
  );

  const verifySession = useCallback(async () => {
    const token = getLocalStorageItem<string>('access_token');
    if (!token) return null;

    const res = await getCurrentUser(token);
    if (!res) return null;
    return res;
  }, [getCurrentUser]);

  const logout = useCallback(async () => {
    // disconnect(config);
    setCurrentUser(null);
    removeLocalStorageItem('access_token');
    removeLocalStorageItem('current_org_urn');
  }, []);

  useEffect(() => {
    const initialize = async () => {
      const token = getLocalStorageItem<string>('access_token');
      if (token) {
        await getCurrentUser(token);
      }
      setLoading(false);
    };

    initialize();
  }, [getCurrentUser]);

  return (
    <AuthContext.Provider
      value={{
        loading: loading,
        isAuthenticated,
        logout,
        user: currentUser,
        verifySession,
        setAccessToken,
        updateMetadata,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
